import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { SproutIcon, TagIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSiteScope } from '../-section'

/**
 * Keywords is more than one reading of one set, so it grows a sidebar rather
 * than a second tab: the tab row is about which report you are in, and these
 * are all the same report asked differently. The layout is pathless so a single
 * keyword's page stays out of it — a detail page is one click deep and has no
 * business being narrowed by a nav it cannot use.
 */
export const Route = createFileRoute('/_app/$org/$host/keywords/_list')({
  component: KeywordsLayout,
})

const SECTIONS = [
  { to: '/$org/$host/keywords', key: 'keywords', label: 'All keywords', icon: TagIcon },
  {
    to: '/$org/$host/keywords/low-hanging',
    key: 'low-hanging',
    label: 'Low hanging',
    icon: SproutIcon,
  },
] as const

function KeywordsLayout() {
  const { org, host, range } = useSiteScope()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const current = pathname.replace(/\/$/, '').split('/').at(-1)

  return (
    <div className="flex flex-col gap-8 sm:flex-row">
      <nav className="flex shrink-0 gap-1 sm:w-44 sm:flex-col">
        {SECTIONS.map((section) => {
          const active = current === section.key
          return (
            <Link
              key={section.key}
              to={section.to}
              params={{ org, host }}
              search={{ range }}
              className={cn(
                'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                active
                  ? 'bg-plum-selected text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <section.icon className={cn('size-3.5', active ? 'text-plum' : 'opacity-70')} />
              {section.label}
            </Link>
          )
        })}
      </nav>

      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
